/* Scandinavian Edition — Product details (Anatomy + Fit studio) */
(() => {
  if (customElements.get('se-product-details')) return;

  const MOBILE = window.matchMedia('(max-width: 749px)');
  /* A click on a modal dialog's backdrop reports the dialog as target; check it was outside the box */
  const outside = (el, e) => {
    const r = el.getBoundingClientRect();
    return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
  };
  const letterFor = (key) => {
    const k = key.toLowerCase();
    if (k.includes('chest')) return 'A';
    if (k.includes('waist')) return 'B';
    if (k.includes('sleeve')) return 'C';
    if (k.includes('back')) return 'D';
    return '';
  };

  class SeProductDetails extends HTMLElement {
    connectedCallback() {
      try {
        this.cfg = JSON.parse(this.querySelector('[data-se-config]')?.textContent || '{}');
      } catch (e) {
        this.cfg = {};
      }
      this.unit = 'cm';
      this.#buildMissingRows();
      this.#initFeatures();
      this.#initTabs();
      this.#initSizes();
      this.#initCards();
      this.#initMeasure();
      this.#initReadMore();
    }

    /* ---------- Fit cards: image (left) and text (middle) change together ---------- */
    #initCards() {
      const slides = Array.from(this.querySelectorAll('.se-pd__fit-slide[data-se-card]'));
      const texts = Array.from(this.querySelectorAll('.se-pd__fit-text[data-se-card]'));
      const count = Math.max(slides.length, texts.length);
      if (count < 2) return;
      let index = 0;
      const show = (i, focusSel) => {
        index = (i + count) % count;
        [slides, texts].forEach((group) =>
          group.forEach((el, n) => {
            el.hidden = n !== index;
            el.toggleAttribute('data-se-enter', n === index);
          })
        );
        clearTimeout(this.cardTimer);
        this.cardTimer = setTimeout(() => this.querySelectorAll('[data-se-enter]').forEach((el) => el.removeAttribute('data-se-enter')), 500);
        if (focusSel) slides[index]?.querySelector(focusSel)?.focus();
      };
      this.showFirstCard = () => show(0);
      slides.forEach((slide) => {
        slide.querySelector('[data-se-card-prev]')?.addEventListener('click', () => show(index - 1, '[data-se-card-prev]'));
        slide.querySelector('[data-se-card-next]')?.addEventListener('click', () => show(index + 1, '[data-se-card-next]'));
      });

      /* Swipe on the image (touch / pen) */
      const media = this.querySelector('.se-pd__fit-media');
      let x0 = null;
      let y0 = null;
      media?.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' || e.target.closest('button')) return;
        x0 = e.clientX;
        y0 = e.clientY;
      });
      media?.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        const dy = e.clientY - y0;
        x0 = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        show(dx < 0 ? index + 1 : index - 1);
      });
      media?.addEventListener('pointercancel', () => (x0 = null));
    }

    /* ---------- Short description: a few lines + "Read more" drawer ---------- */
    #initReadMore() {
      const clamp = this.querySelector('[data-se-more-clamp]');
      const btn = this.querySelector('[data-se-more-open]');
      const drawer = this.querySelector('[data-se-more]');
      if (!clamp || !btn || !drawer) return;
      /* Only offer "Read more" when the text is actually cut off */
      const check = () => {
        btn.hidden = clamp.scrollHeight <= clamp.clientHeight + 1;
      };
      check();
      if ('ResizeObserver' in window) new ResizeObserver(check).observe(clamp);
      this.#fillIntro();
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => this.#fillIntro(), 150);
      });
      document.fonts?.ready.then(() => this.#fillIntro());
      btn.addEventListener('click', () => drawer.showModal());
      drawer.querySelector('[data-se-more-close]')?.addEventListener('click', () => drawer.close());
      drawer.addEventListener('click', (e) => {
        if (e.target === drawer && outside(drawer, e)) drawer.close();
      });
      drawer.addEventListener('close', () => btn.focus({ preventScroll: true }));
    }

    /* Desktop: show as many description lines as fit between the title and the feature list,
       so the text fills the space instead of leaving a gap (never fewer than the setting). */
    #fillIntro() {
      const wrap = this.querySelector('[data-se-more-wrap]');
      const clamp = this.querySelector('[data-se-more-clamp]');
      const content = this.querySelector('.se-pd__anatomy .se-pd__content');
      const intro = this.querySelector('.se-pd__intro');
      const list = this.querySelector('.se-pd__features');
      if (!wrap || !clamp || !content || !intro || !list) return;
      const base = parseInt(wrap.dataset.seMoreBase, 10) || 3;
      if (MOBILE.matches || getComputedStyle(content).justifyContent !== 'space-between') {
        wrap.style.setProperty('--se-more-lines', base);
        list.style.minHeight = '';
        return;
      }
      list.style.minHeight = '';
      /* Reserve room for the tallest feature text, so switching features never pushes things around */
      const bodies = Array.from(list.querySelectorAll('.se-pd__feature-body'));
      const open = bodies.find((b) => !b.hidden);
      const openH = open ? open.offsetHeight : 0;
      let maxH = openH;
      bodies.forEach((b) => {
        if (!b.hidden) return;
        b.hidden = false;
        maxH = Math.max(maxH, b.offsetHeight);
        b.hidden = true;
      });
      /* Keep the list as tall as with the longest feature open: switching features never resizes the images */
      list.style.minHeight = `${list.offsetHeight + (maxH - openH)}px`;
      const lh = parseFloat(getComputedStyle(clamp).lineHeight) || 26;
      const cs = getComputedStyle(content);
      const inner = content.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const minGap = 56;
      const free = inner - intro.offsetHeight - list.offsetHeight - minGap;
      const current = Math.round(clamp.clientHeight / lh);
      const lines = Math.max(base, Math.min(14, current + Math.floor(free / lh)));
      wrap.style.setProperty('--se-more-lines', lines);
    }

    /* ---------- Measurements: in place of the fit text (desktop) or a bottom sheet (mobile) ---------- */
    #initMeasure() {
      const body = this.querySelector('[data-se-fit]');
      const dialog = this.querySelector('[data-se-measure]');
      if (!body || !dialog) return;
      const openers = Array.from(this.querySelectorAll('[data-se-measure-open]'));
      const mainBtn = this.querySelector('.se-pd__measure-btn');

      const setState = (open) => {
        body.dataset.view = open ? 'measure' : 'fit';
        openers.forEach((b) => b.setAttribute('aria-expanded', String(open)));
      };

      this.openMeasure = ({ fromLink = false } = {}) => {
        if (this.tabs?.length) this.#showTab('size');
        if (MOBILE.matches) {
          if (!dialog.open) dialog.showModal();
        } else {
          if (!dialog.open) dialog.show();
          if (fromLink) this.querySelector('#size-guidance')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          dialog.querySelector('.se-pd__back')?.focus({ preventScroll: true });
        }
        setState(true);
      };

      const close = () => {
        const wasModal = dialog.matches(':modal');
        if (dialog.open) dialog.close();
        setState(false);
        if (!wasModal) mainBtn?.focus({ preventScroll: true });
      };

      openers.forEach((b) => b.addEventListener('click', () => this.openMeasure()));
      dialog.querySelectorAll('[data-se-measure-close]').forEach((b) => b.addEventListener('click', close));
      dialog.addEventListener('close', () => setState(false));
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !dialog.matches(':modal')) close();
      });
      /* Tap on the dimmed area closes the sheet */
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog && dialog.matches(':modal') && outside(dialog, e)) close();
      });
      MOBILE.addEventListener?.('change', () => {
        if (dialog.open) close();
      });

      /* Any "#size-guidance" link on the page (e.g. "Size guide" under the size picker):
         both open at "How does it fit?" (mobile: the Size guidance tab; desktop: scroll to Fit studio) */
      document.addEventListener('click', (e) => {
        const link = e.target.closest?.('a[href$="#size-guidance"]');
        if (!link || this.contains(link)) return;
        e.preventDefault();
        if (dialog.open) close();
        this.showFirstCard?.();
        if (MOBILE.matches && this.tabs?.length) this.#showTab('size', true);
        else this.querySelector('#size-guidance')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    /* ---------- Anatomy ----------
       Desktop: active image (left) | list (middle) | next image with overlay (right).
       Arrows, the next image, the list, arrow keys, swipe and horizontal trackpad
       scroll all drive the same index, looping round. */
    #initFeatures() {
      this.anatomy = this.querySelector('[data-se-anatomy]');
      this.features = Array.from(this.querySelectorAll('[data-se-feature]'));
      this.stage = Array.from(this.querySelectorAll('[data-se-stage]'));
      this.peek = Array.from(this.querySelectorAll('[data-se-peek-item]'));
      this.peekButton = this.querySelector('[data-se-peek]');
      this.counter = this.querySelector('[data-se-count]');
      this.live = this.querySelector('[data-se-live]');
      this.activeIndex = 0;
      const count = this.features.length;
      if (!count) return;
      const go = (i) => this.#activateFeature(((i % count) + count) % count, true);
      const next = () => go(this.activeIndex + 1);
      const prev = () => go(this.activeIndex - 1);

      this.features.forEach((li, i) => {
        li.querySelector('.se-pd__feature-button')?.addEventListener('click', () => go(i));
      });
      this.querySelector('[data-se-prev]')?.addEventListener('click', prev);
      this.querySelector('[data-se-next]')?.addEventListener('click', next);
      this.peekButton?.addEventListener('click', next);
      this.#updatePeekLabel();
      this.#initSwipeCounter();
      if (count < 2 || !this.anatomy) return;

      /* Arrow keys while focus is anywhere inside Anatomy (desktop layout only) */
      this.anatomy.addEventListener('keydown', (e) => {
        if (MOBILE.matches || e.altKey || e.ctrlKey || e.metaKey) return;
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        e.key === 'ArrowRight' ? next() : prev();
        /* Keep focus on the list item that is now active, if focus was in the list */
        if (e.target.closest?.('.se-pd__feature-button')) {
          this.features[this.activeIndex]?.querySelector('.se-pd__feature-button')?.focus();
        }
      });

      /* Swipe (touch / pen) on the images */
      [this.querySelector('.se-pd__stage'), this.peekButton].forEach((el) => {
        if (!el) return;
        let x0 = null;
        let y0 = null;
        el.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'mouse') return;
          x0 = e.clientX;
          y0 = e.clientY;
        });
        el.addEventListener('pointerup', (e) => {
          if (x0 === null) return;
          const dx = e.clientX - x0;
          const dy = e.clientY - y0;
          x0 = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          this.swiped = true;
          dx < 0 ? next() : prev();
        });
        el.addEventListener('pointercancel', () => (x0 = null));
        /* A swipe on the peek must not also count as a click */
        el.addEventListener(
          'click',
          (e) => {
            if (!this.swiped) return;
            this.swiped = false;
            e.stopImmediatePropagation();
            e.preventDefault();
          },
          true
        );
      });

      /* Horizontal trackpad scroll on the images: one step per gesture */
      let wheelLock = false;
      let wheelSum = 0;
      let wheelTimer;
      const onWheel = (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => {
          wheelLock = false;
          wheelSum = 0;
        }, 180);
        if (wheelLock) return;
        wheelSum += e.deltaX;
        if (Math.abs(wheelSum) < 30) return;
        wheelLock = true;
        wheelSum > 0 ? next() : prev();
      };
      [this.querySelector('.se-pd__stage'), this.peekButton].forEach((el) =>
        el?.addEventListener('wheel', onWheel, { passive: false })
      );
    }

    /* Mobile: the feature list is a swipe gallery; keep "01 / 04" in step with it */
    #initSwipeCounter() {
      const list = this.querySelector('.se-pd__features');
      const out = this.querySelector('[data-se-swipe-count]');
      if (!list || !out || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const i = this.features.indexOf(entry.target);
            if (i > -1) out.textContent = String(i + 1).padStart(2, '0');
          });
        },
        { root: list, threshold: 0.6 }
      );
      this.features.forEach((li) => io.observe(li));
    }

    #activateFeature(index, announce) {
      this.activeIndex = index;
      const count = this.features.length || 1;
      const nextIndex = (index + 1) % count;
      this.features.forEach((li, i) => {
        const on = i === index;
        li.dataset.active = String(on);
        li.querySelector('.se-pd__feature-button')?.setAttribute('aria-expanded', String(on));
        const body = li.querySelector('.se-pd__feature-body');
        if (body) body.hidden = !on;
      });
      this.stage.forEach((el, i) => {
        el.dataset.active = String(i === index);
        el.setAttribute('aria-hidden', String(i !== index));
      });
      this.peek.forEach((el, i) => (el.dataset.active = String(i === nextIndex)));
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
      this.#updatePeekLabel();
      if (announce && this.live && !MOBILE.matches) {
        const of = this.anatomy?.dataset.seOf || 'of';
        this.live.textContent = `${index + 1} ${of} ${count}: ${this.#titleAt(index)}`;
      }
    }

    #titleAt(i) {
      return this.features[i]?.querySelector('.se-pd__feature-title')?.textContent.trim() || '';
    }

    #updatePeekLabel() {
      if (!this.peekButton) return;
      if (!this.peekLabel) this.peekLabel = this.peekButton.getAttribute('aria-label') || 'Next';
      const n = (this.activeIndex + 1) % (this.features.length || 1);
      this.peekButton.setAttribute('aria-label', `${this.peekLabel}: ${this.#titleAt(n)}`);
    }

    /* ---------- Tabs (mobile only; desktop shows both) ---------- */
    #initTabs() {
      this.tabs = Array.from(this.querySelectorAll('[data-se-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-se-panel]'));
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.#showTab(tab.dataset.seTab));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const i = this.tabs.indexOf(tab);
          const next = this.tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
          this.#showTab(next.dataset.seTab);
          next.focus();
        });
      });

      this.querySelectorAll('[data-se-goto]').forEach((a) =>
        a.addEventListener('click', (e) => {
          if (!MOBILE.matches) return;
          e.preventDefault();
          this.#showTab(a.dataset.seGoto, true);
        })
      );

      if (location.hash === '#size-guidance') this.#showTab('size', true);
    }

    #showTab(name, scroll) {
      this.tabs.forEach((t) => {
        const on = t.dataset.seTab === name;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((p) => (p.dataset.active = String(p.dataset.sePanel === name)));
      if (scroll) {
        const target = MOBILE.matches ? this : this.querySelector(`[data-se-panel="${name}"]`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* ---------- Fit studio ---------- */
    #initSizes() {
      this.sizeButtons = Array.from(this.querySelectorAll('[data-se-size]'));
      this.rowGroups = Array.from(this.querySelectorAll('[data-se-rows]'));
      this.sizeButtons.forEach((b) => b.addEventListener('click', () => this.#selectSize(b.dataset.seSize)));
      this.querySelectorAll('[data-se-unit]').forEach((b) =>
        b.addEventListener('click', () => this.#setUnit(b.dataset.seUnit))
      );
      if (this.rowGroups.length && this.rowGroups.every((g) => g.hidden)) {
        this.#selectSize(this.rowGroups[0].dataset.seRows);
      }
    }

    #selectSize(size) {
      this.sizeButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.seSize === size)));
      this.rowGroups.forEach((g) => (g.hidden = g.dataset.seRows !== size));
    }

    #setUnit(unit) {
      this.unit = unit;
      this.querySelectorAll('[data-se-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.seUnit === unit)));
      this.querySelectorAll('[data-se-cm]').forEach((dd) => {
        const cm = parseFloat(String(dd.dataset.seCm).replace(',', '.'));
        if (Number.isNaN(cm)) return;
        dd.textContent = unit === 'cm' ? `${dd.dataset.seCm} cm` : `${(cm / 2.54).toFixed(1)} in`;
      });
    }

    /* Fallback: if the server could not list the rows, build them from the JSON */
    #buildMissingRows() {
      const data = Array.isArray(this.cfg.measurements) ? this.cfg.measurements : [];
      this.querySelectorAll('[data-se-rows]').forEach((dl) => {
        if (dl.querySelector('.se-pd__row')) return;
        const row = data.find((r) => String(r.size) === dl.dataset.seRows);
        if (!row) return;
        Object.keys(row)
          .filter((k) => k !== 'size')
          .forEach((k) => {
            const wrap = document.createElement('div');
            wrap.className = 'se-pd__row';
            const dt = document.createElement('dt');
            const letter = letterFor(k);
            if (letter) {
              const b = document.createElement('b');
              b.textContent = letter;
              dt.append(b);
            }
            dt.append(k);
            const dd = document.createElement('dd');
            dd.dataset.seCm = row[k];
            dd.textContent = `${row[k]} cm`;
            wrap.append(dt, dd);
            dl.append(wrap);
          });
      });
    }
  }

  customElements.define('se-product-details', SeProductDetails);
})();
